import {
  Checklist,
  DossierTable,
  Field,
  FieldGroup,
  FieldProse,
  Gating,
  NumberedSection,
  Placeholder,
  Section16SoftwareDoc,
  Section17CyberInteropDoc,
  Section6SamdFields,
  hasReal,
  obj,
  prose,
  withInlinePlaceholders,
} from './DossierPrimitives';
import { IvdDeviceDescription, IvdPerformanceBody } from './IvdSections';
import { StructuredCoverLetter } from './CoverLetterSection';

/**
 * eSTAR Premarket Approval (PMA) renderer — 26-section 21 CFR 814.20 spine.
 * (The former §16 "Risks to Health" — a De Novo/eSTAR artifact not named in
 * 814.20 — was removed; its benefit-risk / adverse-effects content lives in §5
 * Summary conclusions (b)(3)(vi) and §15 SSED benefit_risk_determination.)
 * Schema: `backend/schemas_pma.py` (PmaDossier). Spine (single source of
 * truth): `app/dossier/usa/rules/pma_spine.py` (PMA_SPINE). Agent prompt:
 * `SYSTEM_PROMPT_PMA` in `app/dossier/usa/prompts/pma.py`.
 *
 * STRICTLY PMA. This is a Premarket Approval Application, NOT a 510(k): there
 * is NO predicate and NO substantial-equivalence content anywhere. The layout
 * follows 814.20(b)(1)-(14),(a),(d) faithfully — (b)(3) Summary of the PMA,
 * (b)(4) complete device description incl. pictorials, (b)(4)(v) Device
 * Manufacturing Section, (b)(6)(i) consolidated Nonclinical Laboratory Studies,
 * (b)(6)(ii)/(7) Clinical Investigations, SSED, (b)(8) Bibliography, Form FDA
 * 3674, (b)(13) Pediatric Use, (b)(14) Combination Product, (d) omission
 * justification.
 *
 * Schema detection (parent renderer): submission_type === "pma", checked
 * BEFORE the eSTAR detector because PMA + 510(k) + De Novo share the
 * `section_NN_` key naming.
 *
 * MUST stay in FE↔BE lockstep with the backend renderer
 * `_render_estar_pma_sections_new_schema` in
 * `backend/services/dossier/estar_renderer/_pma.py`.
 */
export function PmaRenderer({
  dossier,
  ivd = false,
}: {
  dossier: Record<string, unknown>;
  /** IVD PMA: swap §6 device description for the assay/instrument variant, and render
   *  the analytical battery + reagent stability INSIDE §10 Nonclinical Laboratory
   *  Studies (FDA acceptance checklist A.8.f/A.8.g). There is no added, un-numbered
   *  section — every IVD delta lands on a numbered 814.20 slot. */
  ivd?: boolean;
}) {
  return (
    <div className="space-y-4">
      <Section1 dossier={dossier} />
      <Section2 dossier={dossier} />
      <Section3 dossier={dossier} />
      <Section4 dossier={dossier} />
      <Section5 dossier={dossier} />
      {ivd ? (
        <IvdDeviceDescription dossier={dossier} sectionKey="section_06_device_description" num={6} />
      ) : (
        <Section6 dossier={dossier} />
      )}
      <Section7 dossier={dossier} />
      <Section8 dossier={dossier} />
      <Section9 dossier={dossier} />
      <Section10 dossier={dossier} ivd={ivd} />
      <Section11 dossier={dossier} />
      <Section12 dossier={dossier} />
      <Section13 dossier={dossier} />
      <Section14 dossier={dossier} />
      <Section15 dossier={dossier} />
      <Section16 dossier={dossier} />
      <Section17 dossier={dossier} />
      <Section18 dossier={dossier} />
      <Section19 dossier={dossier} />
      <Section20 dossier={dossier} />
      <Section21 dossier={dossier} />
      <Section22 dossier={dossier} />
      <Section23 dossier={dossier} />
      <Section24 dossier={dossier} />
      <Section25 dossier={dossier} />
      <Section26 dossier={dossier} />
    </div>
  );
}

// ── IVD reagent/calibrator stability, rendered INSIDE §10 beneath the analytical
//    battery (§15 is SSED in the 814.20 spine, so IVD stability has no numbered slot
//    of its own — it is FDA acceptance-checklist item A.8.g, a nonclinical family).
//    Mirrors backend shelf_life_body in _ivd_sections.py. ────────────────────

function IvdStability({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_15_shelf_life');
  return (
    <>
      <Field label="Does the device have a defined shelf life?" value={s.has_shelf_life} />
      <Field label="Shelf life duration:" value={s.shelf_life_duration} />
      <FieldProse label="Shelf life validation:" body={prose(s.shelf_life_validation)} />
      <FieldProse
        label="Reagent / calibrator / control stability:"
        body={prose(s.reagent_stability)}
      />
    </>
  );
}

// Fixed cross-reference text for the A.8.f / A.8.g slots on an IVD — lockstep with
// _A8F_XREF / _A8G_XREF in backend/services/dossier/estar_renderer/_pma_ivd.py.
// Rendered from constants, never from the dossier, so a stray model-written pointer
// can't duplicate the battery below.
const A8F_XREF =
  'Analytical performance for this in vitro diagnostic is presented below under “Analytical Performance (IVD)”.';
const A8G_XREF =
  'Reagent / calibrator / control stability is presented below under “Analytical Performance (IVD)”.';

// ── §1 Submission Type ──────────────────────────────────────────────────

function Section1({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_01_submission_type');
  return (
    <NumberedSection prefix="Section" num={1} title="Submission Type">
      <Field
        label="PMA variant:"
        value={s.submission_type}
        fallbackHint="Original Premarket Approval Application label"
      />
    </NumberedSection>
  );
}

// ── §2 Cover Letter ─────────────────────────────────────────────────────

function Section2({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_02_cover_letter');
  return (
    <NumberedSection prefix="Section" num={2} title="Cover Letter">
      <StructuredCoverLetter
        s2={s}
        submissionTypeRowLabel="Type of PMA Submission"
        reasonHeading="Reason for the PMA Submission"
        reasonPlaceholder="Reason for the PMA submission"
        estarStatement="This electronic signed cover letter is enclosed with an eSTAR PDF as an embedded attachment. The PMA is submitted using the voluntary PMA eSTAR template via the FDA Customer Collaboration Portal (CCP)."
        extraStatements={[
          [
            'Submission Cover Sheet',
            'The CDRH Premarket Review Submission Cover Sheet (Form FDA 3514) is enclosed; any prior submissions for this device are recorded in Form FDA 3514, Section F.',
          ],
          [
            'User Fee Statement',
            'The applicable MDUFA user fee for an original PMA has been paid; the Medical Device User Fee Cover Sheet (Form FDA 3601) and payment identification number are enclosed.',
          ],
          [
            'Administrative Statements',
            'This PMA is submitted as a single electronic version. Information the applicant believes to be trade secret or confidential commercial or financial information is identified as such within the submission (21 CFR 814.20(b)(2)). The applicant will submit periodic safety and effectiveness update reports 3 months after the filing date, following receipt of an approvable letter, and at any other time FDA requests (21 CFR 814.20(e)).',
          ],
        ]}
        usRepresentative
      />
    </NumberedSection>
  );
}

// ── §3 Applicant Information (814.20(b)(1)/(a)) ─────────────────────────

function Section3({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_03_applicant_information');
  return (
    <NumberedSection prefix="Section" num={3} title="Applicant Information">
      <Field label="Applicant (firm) name:" value={s.firm_name} />
      <Field
        label="Establishment registration number:"
        value={s.establishment_registration_number}
      />
      <Field label="Owner/Operator number:" value={s.owner_operator_number} />
      <Field label="Street address:" value={s.street_address} />
      <Field
        label="U.S. Agent (required for foreign applicants):"
        value={s.us_agent}
      />
      <Field
        label="Official correspondent / contact person:"
        value={s.official_correspondent}
      />
    </NumberedSection>
  );
}

// ── §4 Pre-Submission Correspondence & Prior Submissions (gated) ────────

function Section4({ dossier }: { dossier: Record<string, unknown> }) {
  return (
    <NumberedSection
      prefix="Section"
      num={4}
      title="Pre-Submission Correspondence & Prior Submissions"
    >
      <Gating
        alwaysAdvisory
        question="Has the applicant any prior Pre-Submission (Q-Sub), IDE, 510(k), PMA, or other prior FDA interaction for this device?"
        section={obj(dossier, 'section_04_pre_submission_and_prior')}
      />
    </NumberedSection>
  );
}

// ── §5 Summary of the PMA (814.20(b)(3) — the PMA-defining section) ─────

function Section5({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_05_summary_of_pma');
  return (
    <NumberedSection prefix="Section" num={5} title="Summary of the PMA">
      <FieldProse
        label="Indications for use:"
        body={prose(s.indications)}
        fallbackHint="The disease/condition + intended patient population (b)(3)(i)"
      />
      <FieldProse
        label="Device description:"
        body={prose(s.device_description)}
        fallbackHint="How the device functions + its significant characteristics (b)(3)(ii)"
      />
      <FieldProse
        label="Alternative practices and procedures:"
        body={prose(s.alternative_practices)}
        fallbackHint="Existing alternatives + the unmet need (b)(3)(iii)"
      />
      <FieldProse
        label="Marketing history:"
        body={prose(s.marketing_history)}
        fallbackHint="Brief US + non-US marketing history (b)(3)(iv)"
      />
      <FieldProse
        label="Summary of studies:"
        body={prose(s.summary_of_studies)}
        fallbackHint="Abstract of the nonclinical + clinical studies (b)(3)(v)"
      />
      <FieldProse
        label="Conclusions drawn from the studies:"
        body={prose(s.conclusions)}
        fallbackHint="Valid scientific evidence per 21 CFR 860.7 + benefit-risk (b)(3)(vi)"
      />
    </NumberedSection>
  );
}

// ── §6 Complete Device Description (814.20(b)(4)(i)-(iv) — SaMD superset) ─

function Section6({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_06_device_description');
  return (
    <NumberedSection prefix="Section" num={6} title="Device Description">
      <Field label="Device trade name / proprietary name:" value={s.trade_name} />
      <Field label="Common or usual name:" value={s.common_name} />
      <Field label="Models / sizes included in this submission:" value={s.models_included} />
      <Section6SamdFields s={s} />
      {/* (b)(4)(i)/(ii) are REQUIRED elements — always render (placeholder chip when
          empty) so the pictorial/component element is never silently dropped. Only the
          SaMD-vs-hardware-adaptive detailed_description is omit-when-empty. */}
      <FieldProse
        label="Pictorial representations (b)(4)(i):"
        body={prose(s.pictorial_representations)}
        fallbackHint="Device figures / photographs / block diagrams (submitter attachment)"
      />
      <FieldProse
        label="Functional components (b)(4)(ii):"
        body={prose(s.functional_components)}
        fallbackHint="Functional components / ingredients relevant to clinical function"
      />
      <FieldProse
        label="Materials specifications (b)(4)(iii):"
        body={prose(s.materials_specifications)}
        fallbackHint="Material specifications + any color additive (common/chemical name, weight %), or a statement that none is present"
      />
      {hasReal(prose(s.detailed_description)) && (
        <FieldProse label="Detailed device description:" body={prose(s.detailed_description)} />
      )}
    </NumberedSection>
  );
}

// ── §7 Proposed Indications for Use ────────────────────────────────────

function Section7({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_07_indications_for_use');
  return (
    <NumberedSection prefix="Section" num={7} title="Proposed Indications for Use">
      <FieldProse
        label="Indications for Use statement:"
        body={prose(s.indications_statement)}
      />
      <Field label="Prescription use (Rx) — 21 CFR 801.109:" value={s.prescription_use} />
      <Field label="Over-the-counter use:" value={s.otc_use} />
      <Field label="Life-supporting or life-sustaining device?" value={s.life_supporting} />
      <Field label="Intended for pediatric use?" value={s.pediatric_use} />
    </NumberedSection>
  );
}

// ── §8 Device Manufacturing Section (814.20(b)(4)(v) + QMS/PAI) ─────────

function Section8({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_08_device_manufacturing');
  return (
    <NumberedSection prefix="Section" num={8} title="Device Manufacturing Section">
      <FieldProse
        label="Manufacturing facility:"
        body={prose(s.manufacturing_facility)}
        fallbackHint="Facility identification (FEI/CFN), location, manufacturing activity"
      />
      <FieldProse
        label="Process summary:"
        body={prose(s.process_summary)}
        fallbackHint="Methods used to manufacture, process, pack, store, install"
      />
      <FieldProse
        label="In-process controls:"
        body={prose(s.in_process_controls)}
        fallbackHint="Critical control parameters during manufacture"
      />
      <FieldProse
        label="Finished device specifications:"
        body={prose(s.finished_device_specs)}
        fallbackHint="Release specifications + test methods"
      />
      <FieldProse
        label="Quality management system:"
        body={prose(s.quality_system)}
        fallbackHint="ISO 13485:2016 + 21 CFR 820 (QMSR) compliance"
      />
      <FieldProse
        label="Pre-Approval Inspection (PAI) readiness:"
        body={prose(s.pai_readiness)}
        fallbackHint="Narrative of inspection readiness"
      />
    </NumberedSection>
  );
}

// ── §9 Consensus & Performance Standards (814.20(b)(5)) ────────────────

function Section9({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_09_consensus_standards');
  const rows = Array.isArray(s.standards)
    ? (s.standards as Record<string, unknown>[]).map((r) => [
        r.fda_recognition_number,
        r.standard_designation,
        r.title,
        r.conformance,
      ])
    : [];
  return (
    <NumberedSection prefix="Section" num={9} title="Consensus & Performance Standards">
      <FieldGroup label="Standards:">
        <DossierTable
          headers={['FDA Rec. #', 'Standard', 'Title', 'Conformance']}
          rows={rows}
        />
      </FieldGroup>
      <FieldProse
        label="Declaration of Conformity:"
        body={prose(s.declaration_of_conformity)}
      />
    </NumberedSection>
  );
}

// ── §10 Nonclinical Laboratory Studies (814.20(b)(6)(i)) ───────────────

function Section10({
  dossier,
  ivd = false,
}: {
  dossier: Record<string, unknown>;
  ivd?: boolean;
}) {
  const s = obj(dossier, 'section_10_nonclinical_studies');
  // Each acceptance-checklist family (A.8.a-i) ALWAYS renders (placeholder chip when
  // empty) so a required family is never silently dropped.
  const field = (label: string, key: string) => (
    <FieldProse
      label={label}
      body={prose(s[key])}
      fallbackHint="Submitter's test report reference, or a 'Not applicable — [reason]' statement"
    />
  );
  // IVD: A.8.f Chemistry/Analytical and A.8.g Shelf Life are where FDA's acceptance
  // checklist files the analytical battery + reagent stability, so those two slots
  // carry a fixed cross-reference and the battery renders at the foot of the section.
  const fixed = (label: string, text: string) => <FieldProse label={label} body={text} />;
  return (
    <NumberedSection prefix="Section" num={10} title="Nonclinical Laboratory Studies">
      {field('Sterilization:', 'sterilization')}
      {field('Biological / Microbiological:', 'biological_microbiological')}
      {field('Immunological:', 'immunological')}
      {field('Toxicological / Biocompatibility:', 'toxicological_biocompatibility')}
      {field('Engineering (stress / wear):', 'engineering')}
      {ivd ? fixed('Chemistry / Analytical:', A8F_XREF) : field('Chemistry / Analytical:', 'chemistry_analytical')}
      {ivd ? fixed('Shelf life:', A8G_XREF) : field('Shelf life:', 'shelf_life')}
      {field('Animal studies:', 'animal')}
      {field('Other laboratory testing:', 'other')}
      <FieldProse
        label="GLP compliance statement (21 CFR Part 58):"
        body={prose(s.glp_statement)}
        fallbackHint="A statement of GLP compliance, or a brief reason for non-compliance"
      />
      {ivd && (
        <>
          <h4 className="text-[15px] font-bold text-navy mt-5 mb-2 pt-3 border-t border-line">
            Analytical Performance (IVD)
          </h4>
          <IvdPerformanceBody dossier={dossier} sectionKey="analytical_performance" />
          <IvdStability dossier={dossier} />
        </>
      )}
    </NumberedSection>
  );
}

// ── §11 Software/Firmware (FDA §VI documentation; gated) ───────────────

function Section11({ dossier }: { dossier: Record<string, unknown> }) {
  return <Section16SoftwareDoc dossier={dossier} sectionKey="section_11_software_firmware" num={11} />;
}

// ── §12 Cybersecurity / Interoperability (FDA cyber Appendix 4 + interop) ─

function Section12({ dossier }: { dossier: Record<string, unknown> }) {
  return <Section17CyberInteropDoc dossier={dossier} sectionKey="section_12_cybersecurity" num={12} />;
}

// ── §13 EMC / Electrical / Wireless (gated) ────────────────────────────

function Section13({ dossier }: { dossier: Record<string, unknown> }) {
  return (
    <NumberedSection
      prefix="Section"
      num={13}
      title="EMC, Electrical, Mechanical, Wireless and Thermal Safety"
    >
      <Gating
        alwaysAdvisory
        question="Does the device have electrical, electronic, electromechanical, or wireless components?"
        section={obj(dossier, 'section_13_electrical_safety_emc')}
      />
    </NumberedSection>
  );
}

// ── §14 Clinical Investigations (814.20(b)(6)(ii)+(b)(7) — the core) ────

function Section14({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_14_clinical_investigations');
  const ide = obj(s, 'ide_history');
  const pivotal = obj(s, 'pivotal_study');
  const hasPivotalContent = prose(pivotal.study_summary) != null
    || prose(pivotal.efficacy_results) != null;
  return (
    <NumberedSection prefix="Section" num={14} title="Clinical Investigations">
      <Field
        label="Clinical data status:"
        value={s.clinical_data_status}
        fallbackHint="Complete (CSR uploaded) / Incomplete — no CSR uploaded"
      />
      <h4 className="text-[14px] font-bold text-navy mt-3 mb-2">IDE History</h4>
      <FieldProse
        label="IDE summary:"
        body={prose(ide.ide_summary)}
        fallbackHint="FDA approval date, study sponsor, sites count, enrollment count"
      />
      <Field
        label="IDE number(s):"
        value={ide.ide_numbers}
        fallbackHint="Submitter must provide IDE number(s) + FDA approval letter date(s)"
      />
      <h4 className="text-[14px] font-bold text-navy mt-3 mb-2">Pivotal Clinical Study</h4>
      {hasPivotalContent ? (
        <>
          <FieldProse label="Study summary:" body={prose(pivotal.study_summary)} />
          <FieldProse label="Efficacy results:" body={prose(pivotal.efficacy_results)} />
          <FieldProse label="Safety analysis:" body={prose(pivotal.safety_analysis)} />
          <FieldProse
            label="Risk-benefit analysis:"
            body={prose(pivotal.risk_benefit_analysis)}
          />
          <FieldProse
            label="Statistical analysis:"
            body={prose(pivotal.statistical_analysis)}
          />
        </>
      ) : (
        <p className="m-0 my-1 text-[13.5px]">
          <Placeholder hint="Pivotal CSR not uploaded — PMA cannot be filed without pivotal study report" />
        </p>
      )}
    </NumberedSection>
  );
}

// ── §15 SSED (PMA-specific — publicly disclosable) ─────────────────────

function Section15({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_15_ssed');
  return (
    <NumberedSection
      prefix="Section"
      num={15}
      title="Summary of Safety and Effectiveness Data (SSED)"
    >
      <FieldProse
        label="Indications summary:"
        body={prose(s.indications_summary)}
        fallbackHint="Restatement of §7 IFU + target patient population"
      />
      <FieldProse
        label="Safety summary:"
        body={prose(s.safety_summary)}
        fallbackHint="AE/SAE rates, complications — from §14 pivotal study"
      />
      <FieldProse
        label="Effectiveness summary:"
        body={prose(s.effectiveness_summary)}
        fallbackHint="Endpoint outcomes, effect size, durability — from §14 pivotal study"
      />
      <FieldProse
        label="Benefit-risk determination:"
        body={prose(s.benefit_risk_determination)}
        fallbackHint="Synthesis per FDA Factors guidance (2019)"
      />
    </NumberedSection>
  );
}

// (former §16 "Risks to Health" removed — a De Novo/eSTAR artifact not named in
// 814.20; its benefit-risk / adverse-effects content renders in §5 Summary
// conclusions (b)(3)(vi) and §15 SSED benefit_risk_determination.)

// ── §16 Bibliography & Other Data (814.20(b)(8)) ───────────────────────

function Section16({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_16_bibliography');
  const bib = Array.isArray(s.bibliography_entries)
    ? (s.bibliography_entries as unknown[])
    : [];
  return (
    <NumberedSection prefix="Section" num={16} title="Bibliography & Other Data">
      <FieldGroup label="Bibliography of published reports (adverse or supportive):">
        {bib.length > 0 ? (
          <ol className="list-decimal pl-5 m-0 space-y-1 text-[13.5px]">
            {bib.map((e, i) => (
              <ReferenceItem key={i} src={e} />
            ))}
          </ol>
        ) : (
          <FieldProse
            label="Published literature:"
            body={prose(s.none_identified_statement)}
            fallbackHint="A statement that no related safety/effectiveness literature was identified, if none"
          />
        )}
      </FieldGroup>
      <FieldProse
        label="Analysis of other data / commercial experience:"
        body={prose(s.other_data_analysis)}
      />
    </NumberedSection>
  );
}

// ── §17 Labeling (814.20(b)(10)) ───────────────────────────────────────

function Section17({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_17_labeling');
  return (
    <NumberedSection prefix="Section" num={17} title="Labeling">
      <FieldGroup label="Labeling components included:">
        <Checklist items={s.labeling_components as unknown[] | undefined} />
      </FieldGroup>
      <FieldGroup label="Required content checklist (21 CFR 801):">
        <Checklist items={s.required_content_checklist as unknown[] | undefined} />
      </FieldGroup>
      <FieldProse
        label="Device label text:"
        body={prose(s.device_label_text)}
        fallbackHint="Verbatim device label artwork — submitter-provided regulatory artifact"
      />
      <FieldProse
        label="Instructions for Use (IFU) text:"
        body={prose(s.ifu_text)}
        fallbackHint="Verbatim IFU content — submitter-provided regulatory artifact"
      />
    </NumberedSection>
  );
}

// ── §18 Environmental Assessment (814.20(b)(11); gated, default CatEx) ──

function Section18({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_18_environmental_assessment');
  const isYes = String(s.gating_response ?? 'No') === 'Yes';
  return (
    <NumberedSection prefix="Section" num={18} title="Environmental Assessment">
      <Gating
        alwaysAdvisory
        question="Is a full Environmental Assessment required (i.e., the device does NOT qualify for categorical exclusion under 21 CFR 25.34)?"
        section={s}
      />
      {isYes && (
        <FieldProse
          label="Full Environmental Assessment:"
          body={prose(s.full_assessment_body)}
          fallbackHint="Environmental impact analysis per 21 CFR 25.40"
        />
      )}
    </NumberedSection>
  );
}

// ── §19 Financial Certification / Disclosure (814.20(b)(12) / Part 54) ──

function Section19({ dossier }: { dossier: Record<string, unknown> }) {
  return (
    <NumberedSection prefix="Section" num={19} title="Financial Certification / Disclosure">
      <Gating
        alwaysAdvisory
        question="Are clinical studies relied upon (triggering 21 CFR Part 54 financial certification/disclosure — Form FDA 3454/3455)?"
        section={obj(dossier, 'section_19_financial_certification')}
      />
    </NumberedSection>
  );
}

// ── §20 ClinicalTrials.gov Certification (Form FDA 3674) ───────────────

function Section20({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_20_clinicaltrials_certification');
  return (
    <NumberedSection
      prefix="Section"
      num={20}
      title="ClinicalTrials.gov Certification (Form FDA 3674)"
    >
      <Field label="Certification status (Form FDA 3674):" value={s.certification_status} />
      <Field label="ClinicalTrials.gov NCT number(s):" value={s.nct_numbers} />
      <FieldProse
        label="Statement:"
        body={prose(s.statement)}
        fallbackHint="Form FDA 3674 ClinicalTrials.gov certification statement"
      />
    </NumberedSection>
  );
}

// ── §21 Pediatric Use Information (814.20(b)(13) / §515A(a)(2)) ─────────

function Section21({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_21_pediatric_use');
  return (
    <NumberedSection prefix="Section" num={21} title="Pediatric Use Information">
      <FieldProse
        label="Pediatric subpopulations with the condition:"
        body={prose(s.pediatric_subpopulations)}
        fallbackHint="Neonates/infants/children/adolescents with the condition, or a statement that none exists"
      />
      <Field
        label="Number of affected pediatric patients:"
        value={s.number_affected_pediatric}
        fallbackHint="Number of affected pediatric patients, if readily available"
      />
      <FieldProse label="Assessment:" body={prose(s.assessment)} />
    </NumberedSection>
  );
}

// ── §22 Combination Product Provisions (814.20(b)(14) / §503(g); gated) ─

function Section22({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_22_combination_product');
  const isYes = String(s.gating_response ?? 'No') === 'Yes';
  return (
    <NumberedSection prefix="Section" num={22} title="Combination Product Provisions">
      <Gating
        alwaysAdvisory
        question="Is the device a combination product (contains a drug or biological product constituent part under FD&C §503(g))?"
        section={{ gating_response: s.gating_response, rationale: prose(s.rationale) }}
      />
      {isYes && (
        <>
          <FieldProse
            label="Drug/biologic constituent (§503(g)(5)(B)):"
            body={prose(s.drug_constituent)}
          />
          <FieldProse
            label="Patent statement/certification (§503(g)(5)(A)&(C)):"
            body={prose(s.patent_certification)}
          />
        </>
      )}
    </NumberedSection>
  );
}

// ── §23 Omitted Information & Justification (814.20(d)) ─────────────────

function Section23({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_23_omission_justification');
  const omitted = Array.isArray(s.omitted_elements)
    ? (s.omitted_elements as unknown[])
    : [];
  return (
    <NumberedSection prefix="Section" num={23} title="Omitted Information & Justification">
      {omitted.length > 0 && (
        <FieldGroup label="Omitted 814.20(b) elements:">
          <Checklist items={omitted} />
        </FieldGroup>
      )}
      <FieldProse
        label="Justification for omission:"
        body={prose(s.justification)}
        fallbackHint="Justify each omitted element; a valid rationale for omission counts as Present. If none omitted, state so."
      />
    </NumberedSection>
  );
}

// ── §24 Administrative Documentation ───────────────────────────────────

function Section24({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_24_administrative_documentation');
  const tas = obj(s, 'truthful_accurate_statement');
  const samples = obj(s, 'samples_provided');
  return (
    <NumberedSection
      prefix="Section"
      num={24}
      title="Administrative Documentation"
    >
      <h4 className="text-[14px] font-bold text-navy mt-2 mb-2">
        Truthful and Accurate Statement
      </h4>
      <TruthfulAccurateBlock tas={tas} />

      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">
        Samples Provided (21 CFR 814.20(b)(9))
      </h4>
      <Gating
        alwaysAdvisory
        question="Are physical device samples accompanying this submission (per 21 CFR 814.20(b)(9))?"
        section={samples}
      />
    </NumberedSection>
  );
}

function TruthfulAccurateBlock({ tas }: { tas: Record<string, unknown> }) {
  const today = new Date().toISOString().slice(0, 10);
  const statement = tas.statement;
  const lines: { text: string | unknown; hint: string | null }[] = [
    { text: statement, hint: 'Statement of certification' },
    { text: '', hint: null },
    { text: 'Signature: [electronic signature on file]', hint: null },
    { text: `Name: ${(tas.signatory_name as string) ?? ''}`, hint: 'Signatory name' },
    { text: `Title: ${(tas.signatory_title as string) ?? ''}`, hint: 'Signatory title' },
    { text: `Date: ${(tas.date as string) || today}`, hint: null },
  ];
  return (
    <div className="text-[13.5px] leading-[1.6]">
      {lines.map((line, i) => {
        const text =
          line.text == null
            ? ''
            : typeof line.text === 'string'
              ? line.text
              : String(line.text);
        if (text === '' && line.hint == null) {
          return (
            <p key={i} className="m-0 my-1">
              &nbsp;
            </p>
          );
        }
        if (text === '' && line.hint) {
          return (
            <p key={i} className="m-0 my-1">
              <Placeholder hint={line.hint} />
            </p>
          );
        }
        return (
          <p key={i} className="m-0 my-1">
            {withInlinePlaceholders(text)}
          </p>
        );
      })}
    </div>
  );
}

// ── §25 References (inline citations) ──────────────────────────────────

function Section25({ dossier }: { dossier: Record<string, unknown> }) {
  const refs = Array.isArray(dossier.section_25_references)
    ? (dossier.section_25_references as unknown[])
    : [];
  return (
    <NumberedSection prefix="Section" num={25} title="References">
      <FieldGroup
        label="References:"
        fallbackHint="Literature references and FDA records cited in this submission"
      >
        {refs.length > 0 ? (
          <ol className="list-decimal pl-5 m-0 space-y-1 text-[13.5px]">
            {refs.map((s, i) => (
              <ReferenceItem key={i} src={s} />
            ))}
          </ol>
        ) : null}
      </FieldGroup>
    </NumberedSection>
  );
}

function ReferenceItem({ src }: { src: unknown }) {
  if (!src || typeof src !== 'object') return <li>{String(src)}</li>;
  const s = src as {
    identifier?: string;
    title?: string;
    url?: string;
    tool?: string;
    note?: string;
  };
  return (
    <li>
      {s.identifier && (
        <span className="font-mono text-[12.5px] text-electric mr-1">
          {s.identifier}
        </span>
      )}
      {s.url && s.title ? (
        <a
          href={s.url}
          target="_blank"
          rel="noopener"
          className="text-navy underline decoration-line-strong hover:decoration-electric"
        >
          {s.title}
        </a>
      ) : s.url ? (
        <a
          href={s.url}
          target="_blank"
          rel="noopener"
          className="text-navy underline decoration-line-strong hover:decoration-electric"
        >
          {s.url}
        </a>
      ) : s.title ? (
        <span>{s.title}</span>
      ) : null}
      {s.tool && (
        <span className="ml-2 text-[10.5px] font-bold uppercase tracking-[0.1em] text-muted bg-[#f3f4f6] border border-line rounded-full py-0.5 px-2">
          {s.tool}
        </span>
      )}
      {s.note && <span className="text-muted"> — {s.note}</span>}
    </li>
  );
}

// ── §26 Amendment/Additional Information (AI) Response (gated) ──────────

function Section26({ dossier }: { dossier: Record<string, unknown> }) {
  return (
    <NumberedSection
      prefix="Section"
      num={26}
      title="Amendment/Additional Information (AI) Response"
    >
      <Gating
        alwaysAdvisory
        question="Is this submission a response to an FDA Additional Information request?"
        section={obj(dossier, 'section_26_amendment_response')}
      />
    </NumberedSection>
  );
}
